"use client";

import { useTranslations } from "next-intl";
import type { FontOption } from "./data";
import { assignRoles, contrastText } from "./helpers";

export function DesktopPreview({
  colors,
  font,
}: {
  colors: string[];
  font: FontOption;
}) {
  const t = useTranslations("catalog.preview");
  const { bg, accent, secondary, surface } = assignRoles(colors);
  const bgText = contrastText(bg);
  const accentText = contrastText(accent);

  return (
    <div
      className="overflow-hidden rounded-xl border transition-all duration-500"
      style={{ borderColor: "rgba(245,245,247,0.08)" }}
    >
      {/* browser chrome */}
      <div className="flex items-center gap-1.5 bg-[#1a1a24] px-3 py-2">
        <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
        <div
          className="mx-auto flex items-center gap-1.5 rounded px-3 py-0.5 text-[10px] text-[#8B8B94]"
          style={{ background: "#111118" }}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#34D399] opacity-70" />
          {t("siteUrl")}
        </div>
      </div>
      {/* content */}
      <div
        style={{
          background: bg,
          fontFamily: `"${font.name}", sans-serif`,
          color: bgText,
        }}
        className="transition-all duration-500"
      >
        <nav
          style={{
            background: `${bg}ee`,
            borderBottom: `1px solid ${secondary}22`,
          }}
          className="flex items-center justify-between px-4 py-2.5"
        >
          <span
            style={{ color: surface, fontWeight: 700 }}
            className="text-sm tracking-tight"
          >
            {t("brandName")}
          </span>
          <div
            className="hidden gap-2 text-[8px] sm:flex"
            style={{ color: `${bgText}77` }}
          >
            {[t("navHome"), t("navWork"), t("navBlog")].map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
          <button
            style={{ background: accent, color: accentText }}
            className="rounded px-3 py-1 text-[10px] font-semibold"
          >
            {t("ctaSecondary")}
          </button>
        </nav>
        <div className="px-4 py-5">
          <div
            className="mb-2 inline-block rounded-full px-2 py-0.5 text-[8px] font-semibold uppercase tracking-widest"
            style={{ background: `${accent}22`, color: accent }}
          >
            {t("eyebrow")}
          </div>
          <h1
            className="mb-2 text-lg font-bold leading-snug tracking-tight"
            style={{ color: surface }}
          >
            {t("headline")}{" "}
            <span style={{ color: accent }}>{t("headlineAccent")}</span>
          </h1>
          <p
            className="mb-3 text-[10px] leading-relaxed"
            style={{ color: `${bgText}99` }}
          >
            {t("lede")}
          </p>
          <div className="flex gap-2">
            <button
              style={{ background: accent, color: accentText }}
              className="rounded-lg px-3 py-1.5 text-[10px] font-semibold"
            >
              {t("ctaPrimary")}
            </button>
            <button
              style={{ border: `1px solid ${secondary}44`, color: surface }}
              className="rounded-lg px-3 py-1.5 text-[10px] font-semibold"
            >
              {t("ctaSecondary")}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 px-4 pb-5">
          {(
            [
              ["⚡", t("card1Title"), t("card1Desc")],
              ["✦", t("card2Title"), t("card2Desc")],
              ["◎", t("card3Title"), t("card3Desc")],
            ] as [string, string, string][]
          ).map(([icon, title, desc]) => (
            <div
              key={title}
              style={{
                background: `${surface}18`,
                border: `1px solid ${secondary}22`,
              }}
              className="rounded-lg p-2.5"
            >
              <div className="mb-1 text-sm">{icon}</div>
              <div
                className="mb-0.5 text-[10px] font-semibold"
                style={{ color: surface }}
              >
                {title}
              </div>
              <div className="text-[8px]" style={{ color: `${bgText}66` }}>
                {desc}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
